import React, { useMemo } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { Handle, Position, useStore } from "@xyflow/react";
import { NodeControls } from "./nodeControls";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// Tool logos/icons mapping

// Tool-specific validation logic
const TOOL_VALIDATORS = {
  openai: (settings) => {
    const hasModel = settings?.modelName && settings.modelName.trim().length > 0;
    const hasApiKey = settings?.apiKey && settings.apiKey.trim().length > 0;
    return {
      hasModel,
      hasApiKey,
      isConfigured: hasModel && hasApiKey,
      validationMessage: !hasModel ? "Model not selected" : !hasApiKey ? "API key required" : null,
    };
  },
  anthropic: (settings) => {
    const hasModel = settings?.modelName && settings.modelName.trim().length > 0;
    const hasApiKey = settings?.apiKey && settings.apiKey.trim().length > 0;
    return {
      hasModel,
      hasApiKey,
      isConfigured: hasModel && hasApiKey,
      validationMessage: !hasModel ? "Model not selected" : !hasApiKey ? "API key required" : null,
    };
  },
  google: (settings) => {
    const hasModel = settings?.modelName && settings.modelName.trim().length > 0;
    const hasCredentials = settings?.credentials || settings?.apiKey;
    return {
      hasModel,
      hasCredentials,
      isConfigured: hasModel && hasCredentials,
      validationMessage: !hasModel
        ? "Model not selected"
        : !hasCredentials
          ? "Credentials required"
          : null,
    };
  },
  aws: (settings) => {
    const hasModel = settings?.modelName && settings.modelName.trim().length > 0;
    const hasRegion = settings?.region && settings.region.trim().length > 0;
    const hasCredentials =
      (settings?.accessKeyId && settings?.secretAccessKey) || settings?.roleArn;
    return {
      hasModel,
      hasRegion,
      hasCredentials,
      isConfigured: hasModel && hasRegion && hasCredentials,
      validationMessage: !hasModel
        ? "Model not selected"
        : !hasRegion
          ? "Region required"
          : !hasCredentials
            ? "Credentials required"
            : null,
    };
  },
  default: (settings) => {
    // Generic validation - just check if some key settings exist
    const hasMinimalConfig = Object.keys(settings || {}).length > 0;
    return {
      isConfigured: hasMinimalConfig,
      validationMessage: !hasMinimalConfig ? "Tool not configured" : null,
    };
  },
};

// Tool display names
const TOOL_NAMES = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google AI",
  aws: "AWS Bedrock",
  azure: "Azure OpenAI",
};

export const DefaultToolNode = ({ id, data, selected, highlighted }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  // Get nodes and edges from store for validation
  const nodes = useStore((store) => store.nodes);
  const edges = useStore((store) => store.edges);
  const node = useStore((store) => store.nodes.find((n) => n.id === id));

  // Extract tool type from data
  const toolType = useMemo(() => {
    // Extract from data.type (e.g., "ai.openai" -> "openai")
    if (data.type && data.type.includes(".")) {
      return data.type.split(".").pop().toLowerCase();
    }
    // Fallback to toolType property or default
    return data.toolType?.toLowerCase() || "default";
  }, [data.type, data.toolType]);

  // Get tool display name
  const toolName = useMemo(() => {
    return TOOL_NAMES[toolType] || data.label || "AI Tool";
  }, [toolType, data.label]);

  // Settings validation using tool-specific validator
  const settingsValidation = useMemo(() => {
    const settings = data.output?.settings || {};
    const validator = TOOL_VALIDATORS[toolType] || TOOL_VALIDATORS.default;
    return validator(settings);
  }, [data.output?.settings, toolType]);

  // Execution validation
  const canExecute = useMemo(() => {
    return settingsValidation.isConfigured;
  }, [settingsValidation]);

  // Input validation - check if required inputs are connected
  const inputValidation = useMemo(() => {
    const requiredInputs = data.model?.inputs || [];
    const connectedInputs = edges.filter((edge) => edge.target === id);

    const hasRequiredInputs = requiredInputs.length === 0 || connectedInputs.length > 0;

    return {
      hasRequiredInputs,
      validationMessage: !hasRequiredInputs ? "Input connection required" : null,
    };
  }, [data.model?.inputs, edges, id]);

  // Overall validation
  const overallValidation = useMemo(() => {
    const isValid = settingsValidation.isConfigured && inputValidation.hasRequiredInputs;
    const validationMessage =
      settingsValidation.validationMessage || inputValidation.validationMessage || null;

    return {
      isValid,
      validationMessage,
    };
  }, [settingsValidation, inputValidation]);

  // Status-based styling
  const getStatusStyling = () => {
    const baseColor = selected ? "#27BBD9" : highlighted ? "#70C930" : "#666";
    const currentStatus = data.status || node?.status;

    switch (currentStatus) {
      case "running":
        return {
          borderColor: theme.palette.warning.main,
          glowColor: `${theme.palette.warning.main}30`,
          statusColor: theme.palette.warning.main,
          statusLabel: "Running",
        };
      case "pending":
        return {
          borderColor: theme.palette.info.main,
          glowColor: `${theme.palette.info.main}30`,
          statusColor: theme.palette.info.main,
          statusLabel: "Pending",
        };
      case "completed":
        return {
          borderColor: theme.palette.success.main,
          glowColor: `${theme.palette.success.main}30`,
          statusColor: theme.palette.success.main,
          statusLabel: "Completed",
        };
      case "error":
        return {
          borderColor: theme.palette.error.main,
          glowColor: `${theme.palette.error.main}30`,
          statusColor: theme.palette.error.main,
          statusLabel: "Error",
        };
      case "cancelled":
        return {
          borderColor: theme.palette.grey[500],
          glowColor: `${theme.palette.grey[500]}30`,
          statusColor: theme.palette.grey[500],
          statusLabel: "Cancelled",
        };
      default:
        return {
          borderColor: baseColor,
          glowColor: "transparent",
          statusColor: null,
          statusLabel: null,
        };
    }
  };

  const statusStyling = getStatusStyling();

  // Status badge renderer
  const renderStatusBadge = () => {
    const currentStatus = data.status || node?.status;
    if (!currentStatus || currentStatus === "idle") return null;

    const getStatusIcon = () => {
      switch (currentStatus) {
        case "running":
          return <CircularProgress size={12} sx={{ color: statusStyling.statusColor }} />;
        case "pending":
          return <PlayArrowIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        case "completed":
          return <CheckCircleIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        case "error":
          return <ErrorIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        case "cancelled":
          return <CancelIcon sx={{ fontSize: 14, color: statusStyling.statusColor }} />;
        default:
          return null;
      }
    };

    return (
      <Box
        sx={{
          position: "absolute",
          top: -6,
          right: -6,
          backgroundColor: "#414243",
          borderRadius: "50%",
          border: `2px solid ${statusStyling.statusColor}`,
          width: 20,
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          boxShadow: `0 2px 8px ${statusStyling.glowColor}`,
          transition: "all 0.2s ease-in-out",
        }}
        role="status"
        aria-label={statusStyling.statusLabel}
        title={statusStyling.statusLabel}
      >
        {getStatusIcon()}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#222",
        color: "white",
        borderRadius: 55,
        alignContent: "center",
        alignItems: "center",
        alignSelf: "center",
        textAlign: "center",
        paddingTop: 1,
        width: 90,
        height: 90,
        position: "relative",
        border: `2px solid ${statusStyling.borderColor}`,
        boxShadow:
          statusStyling.glowColor !== "transparent"
            ? `0 0 12px ${statusStyling.glowColor}`
            : "none",
        transition: "border 0.2s, box-shadow 0.2s",
        "&:hover .node-controls": { opacity: 1, pointerEvents: "auto" },
        userSelect: "none",
      }}
    >
      {/* Status Badge */}
      {renderStatusBadge()}

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Top}
        id="output"
        style={{
          backgroundColor: "#00C49F",
          width: 14,
          height: 14,
          borderRadius: "50%",
          left: "50%",
          top: -7,
          transform: "translateX(-50%)",
          border: "2px solid white",
          cursor: "pointer",
          zIndex: 10,
        }}
      />

      {/* Node Controls */}
      {node?.status !== "running" && (
        <NodeControls
          onBottom={true}
          nodeId={id}
          onSettingsClick={() => data.onSettingsClick?.(id)}
          onDelete={() => data.onDelete?.(id)}
          onRun={canExecute ? () => data.onRun?.(id) : undefined}
        />
      )}

      {/* Tool Icon */}
      {data.icon && (
        <Box>
          <img
            src={require(`../../../../assets/${data.icon}.png`)}
            alt="Node Icon"
            style={{ width: "50px", height: "50px", borderRadius: "50%" }}
          />
        </Box>
      )}

      {/* Warning icon for missing configuration */}
      {!overallValidation.isValid && (
        <Box sx={{ position: "absolute", top: -18, right: -5 }}>
          <Tooltip title={overallValidation.validationMessage || "Tool not fully configured"}>
            <ErrorOutlineIcon
              sx={{
                color: "#ff6b6b",
                position: "absolute",
                top: 8,
                right: 8,
                fontSize: 16,
                cursor: "default",
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Execution Results Badge */}
      {data.executionState?.recordCount && (
        <Box
          sx={{
            position: "absolute",
            top: -8,
            left: -8,
            backgroundColor: "#4caf50",
            color: "white",
            borderRadius: "12px",
            padding: "2px 6px",
            fontSize: "10px",
            fontWeight: "bold",
            zIndex: 15,
            border: "1px solid white",
          }}
        >
          {data.executionState.recordCount}
        </Box>
      )}
    </Box>
  );
};
